import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'pl', label: 'Polski' },
];

export default function LanguageSwitcher({ light = false }: { light?: boolean }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const resolved = i18n.resolvedLanguage ?? i18n.language;
  const current = languages.find((lang) => lang.code === resolved) ?? languages[0];

  const btnClass = light
    ? 'text-background-50 hover:bg-background-50/10'
    : 'text-foreground-700 hover:bg-background-200';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium cursor-pointer transition-colors ${btnClass}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-global-line text-base" aria-hidden="true"></i>
        </span>
        <span>{current.label}</span>
        <span className="w-4 h-4 flex items-center justify-center">
          <i className={`ri-arrow-down-s-line text-base transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true"></i>
        </span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-44 rounded-lg border border-background-200 bg-background-50 py-1 z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              role="option"
              aria-selected={lang.code === resolved}
              onClick={() => {
                i18n.changeLanguage(lang.code);
                setOpen(false);
              }}
              className={`flex w-full items-center justify-between px-4 py-2 text-sm cursor-pointer whitespace-nowrap transition-colors ${
                lang.code === resolved
                  ? 'text-primary-700 bg-primary-50 font-semibold'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              <span>{lang.label}</span>
              {lang.code === resolved && (
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line text-base text-primary-600" aria-hidden="true"></i>
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}